import React, { useEffect } from 'react';
import { Layout, Menu, Button, Typography, Drawer } from 'antd';
import {
  DashboardOutlined,
  FileTextOutlined,
  TeamOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  SettingOutlined,
  BankOutlined,
  ShareAltOutlined,
  CalendarOutlined,
  AppstoreAddOutlined,
  ToolOutlined,
  SyncOutlined,
  ShoppingCartOutlined,
  ContainerOutlined,
  BuildOutlined,
  FundOutlined,
  AccountBookOutlined,
  DollarOutlined,
  ApiOutlined,
  CloudUploadOutlined,
  GoldOutlined,
  BookOutlined,
  WalletOutlined,
  SwapOutlined,
  SafetyOutlined,
  AuditOutlined,
  BarChartOutlined,
  CreditCardOutlined,
  ExperimentOutlined,
  PhoneOutlined,
  MobileOutlined,
  WhatsAppOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { NavLink, useLocation } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../context/AuthContext';
import BackendUrlIndicator from '../UI/BackendUrlIndicator';
import "bootstrap/dist/css/bootstrap.min.css";

const { Sider } = Layout;

const drawerWidth = 260;
const collapsedWidth = 72;

const Sidebar = ({ collapsed, onToggle }) => {
  const location = useLocation();
  const { user, refreshProfile } = useAuth();
  const { can, canViewModule, canViewField, isWildcardAdmin } = usePermissions();

  // [fine-grained module, legacy purityAnalyzer action] for every lab page.
  const PURITY_PAGES = [
    ['purityDashboard', 'viewDashboard'], ['purityAssay', 'runTest'],
    ['purityTests', 'viewTests'], ['purityCalibrate', 'calibrate'],
    ['purityCalibrations', 'viewCalibrations'], ['purityXrf', 'verifyXrf'],
    ['purityVersions', 'activateCalibration'], ['purityAnalytics', 'viewAnalytics'],
    ['purityMachines', 'manageMachines'], ['puritySettings', 'manageDeductionSettings'],
  ];
  const purityCan = (module, action, legacyAction) =>
    can(module, action) || can('purityAnalyzer', legacyAction);

  const menuItems = [
    (!user?.dashboard?.key && canViewModule('dashboard')) && { key: '/dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
    (user?.dashboard?.key && canViewModule('dashboard')) && { key: '/module-dashboards', label: 'MyDashBoard', icon: <DashboardOutlined /> },
    (canViewModule('myLeads') || canViewModule('leadManagement') || canViewModule('importLeads') || canViewModule('leadAnalytics') || canViewModule('leadTypes') || canViewModule('leadSources') || canViewModule('leadStatuses')) && {
      key: 'leads-group',
      label: 'Leads / CSR',
      icon: <TeamOutlined />,
      children: [
        canViewModule('myLeads') && { key: '/leads', label: 'My Leads', icon: <UserOutlined /> },
        canViewModule('leadManagement') && { key: '/leads/manage', label: 'Manage Leads', icon: <TeamOutlined /> },
        canViewModule('importLeads') && { key: '/leads/import', label: 'Import Leads', icon: <AppstoreAddOutlined /> },
        canViewModule('leadAnalytics') && { key: '/leads/analytics', label: 'Lead Analytics', icon: <BarChartOutlined /> },
        canViewModule('leadTypes') && { key: '/leads/types', label: 'Lead Types', icon: <ToolOutlined /> },
        canViewModule('leadSources') && { key: '/leads/sources', label: 'Lead Sources', icon: <ToolOutlined /> },
        canViewModule('leadStatuses') && { key: '/leads/statuses', label: 'Lead Statuses', icon: <ToolOutlined /> },
      ].filter(Boolean),
    },
    (canViewModule('callAnalytics') || canViewModule('callDevices') || canViewModule('callTargets')) && {
      key: 'calling-group',
      label: 'Calling',
      icon: <PhoneOutlined />,
      children: [
        canViewModule('callAnalytics') && { key: '/calling/dashboard', label: 'Calling Dashboard', icon: <BarChartOutlined /> },
        canViewModule('callDevices') && { key: '/calling/devices', label: 'Call Devices', icon: <MobileOutlined /> },
        canViewModule('callTargets') && { key: '/calling/targets', label: 'Calling Targets', icon: <ToolOutlined /> },
      ].filter(Boolean),
    },
    // WhatsApp: inbox for any user with the module (admins always), settings for admins.
    (canViewModule('whatsapp') || isWildcardAdmin) && {
      key: 'whatsapp-group',
      label: 'WhatsApp',
      icon: <WhatsAppOutlined />,
      children: [
        { key: '/whatsapp/inbox', label: 'Inbox', icon: <MessageOutlined /> },
        isWildcardAdmin && { key: '/whatsapp/settings', label: 'Bot Settings', icon: <SettingOutlined /> },
      ].filter(Boolean),
    },
    // canViewModule('billing') && { key: '/billing', label: 'Billing', icon: <FileTextOutlined /> },
    canViewModule('branches') && { key: '/branches', label: 'Branches', icon: <BankOutlined /> },
    canViewModule('customer') && { key: '/customers', label: 'Customers', icon: <TeamOutlined /> },
    // canViewModule('user') && { key: '/users', label: 'User List', icon: <TeamOutlined /> },
    can('security', 'view') && {
      key: '/security',
      label: 'Security',
      icon: <SafetyOutlined />,
    },
    can('permission', 'view') && {
      key: '/permission-sets',
      label: 'Permission Set',
      icon: <TeamOutlined />,
    },
    can('permission', 'edit') && {
      key: '/module-dashboards/builder',
      label: 'Dashboard Builder',
      icon: <AppstoreAddOutlined />,
    },
    //  canViewModule('scheme') && { key: '/scheme', label: 'Schemes', icon: <FileTextOutlined /> },
    canViewModule('pledgeReleaseCase') && { key: '/pledge-release-cases', label: 'Pledge Release', icon: <BankOutlined /> },
    canViewModule('walkInCase') && { key: '/walk-in-cases', label: 'Walk-In Cases', icon: <ShoppingCartOutlined /> },
    (canViewModule('goldInventory') || canViewModule('inventoryBatch')) && { key: '/gold-inventory', label: 'Inventory', icon: <ContainerOutlined /> },
    canViewModule('inventoryBranchVault') && {
      key: '/vault',
      label: 'Gold Vault',
      icon: <SafetyOutlined />,
      children: [
        canViewModule('inventoryPurchaseLedger') && { key: '/vault/purchase-ledger', label: 'Purchase Ledger', icon: <FileTextOutlined /> },
        canViewModule('inventoryBranchVault') && { key: '/vault/branch-vault', label: 'Branch Vault', icon: <SafetyOutlined /> },
        canViewModule('inventoryTransfer') && { key: '/vault/transfers', label: 'Transfers', icon: <SwapOutlined /> },
        canViewModule('inventoryHoVault') && { key: '/vault/ho-vault', label: 'HO Vault', icon: <BankOutlined /> },
        canViewModule('inventoryTransferApproval') && { key: '/vault/transfer-approvals', label: 'Transfer Approvals', icon: <UserOutlined /> },
        canViewModule('inventoryTransferReceipt') && { key: '/vault/transfer-receipts', label: 'Transfer Receipts', icon: <ContainerOutlined /> },
        canViewModule('inventoryReports') && { key: '/vault/reconciliation', label: 'Reconciliation', icon: <SyncOutlined /> },
        canViewModule('inventoryReports') && { key: '/vault/aging', label: 'Aging Report', icon: <CalendarOutlined /> },
        canViewModule('inventoryReports') && { key: '/vault/explorer', label: 'Explorer', icon: <DashboardOutlined /> },
        canViewModule('inventoryAudit') && { key: '/vault/audit-log', label: 'Audit Log', icon: <AuditOutlined /> },
      ].filter(Boolean),
    },
    // Each lab page has its own module (purityDashboard, purityAssay, …) and
    // also answers to the legacy whole-module purityAnalyzer action, so an
    // existing purityAnalyzer grant still opens exactly what it used to.
    (canViewModule('purityAnalyzer') || PURITY_PAGES.some(([m]) => canViewModule(m))) && {
      key: '/purity',
      label: 'Purity Analyzer',
      icon: <ExperimentOutlined />,
      children: [
        purityCan('purityDashboard', 'view', 'viewDashboard') && { key: '/purity', label: 'Lab Dashboard', icon: <DashboardOutlined /> },
        purityCan('purityAssay', 'create', 'runTest') && { key: '/purity/run', label: 'Assaying', icon: <ExperimentOutlined /> },
        purityCan('purityTests', 'view', 'viewTests') && { key: '/purity/tests', label: 'Test History', icon: <FileTextOutlined /> },
        purityCan('purityCalibrate', 'create', 'calibrate') && { key: '/purity/calibrate', label: 'Calibrate', icon: <ToolOutlined /> },
        purityCan('purityCalibrations', 'view', 'viewCalibrations') && { key: '/purity/calibrations', label: 'Calibration History', icon: <SyncOutlined /> },
        purityCan('purityXrf', 'verify', 'verifyXrf') && { key: '/purity/xrf', label: 'XRF & Learning', icon: <SafetyOutlined /> },
        purityCan('purityVersions', 'view', 'activateCalibration') && { key: '/purity/versions', label: 'Dataset Versions', icon: <BuildOutlined /> },
        purityCan('purityAnalytics', 'view', 'viewAnalytics') && { key: '/purity/analytics', label: 'Analytics', icon: <BarChartOutlined /> },
        purityCan('purityMachines', 'view', 'manageMachines') && { key: '/purity/machines', label: 'Machines', icon: <ToolOutlined /> },
        purityCan('puritySettings', 'view', 'manageDeductionSettings') && { key: '/purity/settings', label: 'Configure', icon: <SettingOutlined /> },
      ].filter(Boolean),
    },
    // `inventoryDispatch` / `inventoryReceipt` are the fine-grained alternatives
    // to the whole-of-smelter module: they open the dispatch flow for a clerk
    // who has no business in the smelter directory or the GST ledger.
    (canViewModule('smelter') || canViewModule('inventoryDispatch') || canViewModule('inventoryReceipt')) && {
      key: '/smelters',
      label: 'Smelters',
      icon: <BuildOutlined />,
      children: [
        canViewModule('smelter') && { key: '/smelters', label: 'Directory', icon: <BuildOutlined /> },
        canViewModule('smelter') && { key: '/smelters/performance', label: 'Performance', icon: <BarChartOutlined /> },
        (canViewModule('smelter') || canViewModule('inventoryDispatch') || canViewModule('inventoryReceipt'))
          && { key: '/smelters/dispatches', label: 'Dispatches', icon: <SwapOutlined /> },
        (canViewModule('smelter') || canViewModule('inventoryDispatch'))
          && { key: '/smelters/fly-invoice', label: 'Generate Invoice', icon: <FileTextOutlined /> },
        canViewModule('smelter') && { key: '/smelters/transit-memos', label: 'Transit Memos', icon: <FileTextOutlined /> },
        canViewModule('smelter') && { key: '/smelters/gst-ledger', label: 'GST Recovery', icon: <AccountBookOutlined /> },
      ].filter(Boolean),
    },
    canViewModule('financials') && { key: '/financials', label: 'Financials', icon: <FundOutlined /> },
    canViewModule('accounting') && { key: '/accounting', label: 'Accounting', icon: <AccountBookOutlined /> },
    // Need to remove // canViewModule('accounting') && { key: '/manual-journal', label: 'Manual Journal', icon: <BookOutlined /> },
    canViewModule('agents') && {
      key: '/agents',
      label: 'Agents',
      icon: <TeamOutlined />,
      children: [
        { key: '/agents', label: 'Agent List', icon: <TeamOutlined /> },
        { key: '/agents/settlements', label: 'Settlements', icon: <DollarOutlined /> },
      ],
    },
    (canViewModule('workflow') || canViewModule('approval') || can('workflow', 'myApprovals')) && {
      key: '/approvals',
      label: 'Deviation',
      icon: <UserOutlined />,
      children: [
        { key: '/approvals/my-approvals', label: 'Approvals', icon: <SettingOutlined /> },
        canViewModule('treasuryApprovals') && { key: '/treasury/payment-requests', label: 'Payment Approvals', icon: <FileTextOutlined /> },
        (canViewModule('approval') || canViewModule('workflow')) && { key: '/approvals/pending', label: 'Pending', icon: <SettingOutlined /> },
        (can('workflow', 'rules') || canViewModule('workflow')) && { key: '/approvals/rules', label: 'Rules', icon: <TeamOutlined /> },
      ].filter(Boolean),
    },
    //Need to remove SMM
    // canViewModule('smm') && {
    //   key: '/smm',
    //   label: 'SMM',
    //   icon: <ShareAltOutlined />,
    //   children: [
    //     { key: '/smm/compose', label: 'Compose', icon: <AppstoreAddOutlined /> },
    //     { key: '/smm/calendar', label: 'Calendar', icon: <CalendarOutlined /> },
    //     { key: '/smm/library', label: 'Library', icon: <FileTextOutlined /> },
    //     { key: '/smm/settings', label: 'Settings', icon: <ToolOutlined /> },
    //   ],
    // },
    canViewModule('treasury') && {
      key: '/treasury',
      label: 'IBCT',
      icon: <WalletOutlined />,
      children: [
        canViewModule('treasuryHoDashboard') && { key: '/treasury/ho-dashboard', label: 'HO Dashboard', icon: <DashboardOutlined /> },
        canViewModule('treasuryBranchFund') && { key: '/treasury/branch-fund', label: 'Branch Fund', icon: <WalletOutlined /> },
        canViewModule('treasuryBranchFund') && { key: '/treasury/branch-expenses', label: 'Branch Expenses', icon: <CreditCardOutlined /> },
        canViewModule('treasuryCashLedger') && { key: '/treasury/cash-ledger', label: 'Cash Ledger', icon: <AccountBookOutlined /> },
        canViewModule('treasuryCentralFund') && { key: '/treasury/central-fund', label: 'Central Fund', icon: <BankOutlined /> },
        canViewModule('treasuryCentralFund') && { key: '/treasury/funding-history', label: 'Funding History', icon: <FileTextOutlined /> },
        canViewModule('treasury') && { key: '/treasury/transfers', label: 'Cash Transfers', icon: <SwapOutlined /> },
        canViewModule('treasuryHoDashboard') && { key: '/treasury/all-transfers', label: 'All Transfers', icon: <SwapOutlined /> },
        canViewModule('treasury') && { key: '/treasury/day-status', label: 'Day Status', icon: <CalendarOutlined /> },
        canViewModule('treasuryDayClosure') && { key: '/treasury/eod-closure', label: 'EOD Closure', icon: <FundOutlined /> },
        (canViewModule('treasuryApprovals') || canViewModule('treasuryDayClosure')) && { key: '/treasury/closure-approvals', label: 'Closure Approvals', icon: <SettingOutlined /> },
      ].filter(Boolean),
    },
    canViewModule('hr') && {
      key: '/hr',
      label: 'HR & Admin',
      icon: <TeamOutlined />,
      children: [
        canViewModule('hrDashboard') && { key: '/hr/dashboard', label: 'HR Dashboard', icon: <DashboardOutlined /> },
        canViewModule('hrEmployees') && { key: '/hr/employees', label: 'Employees', icon: <TeamOutlined /> },
        canViewModule('hrAttendance') && { key: '/hr/attendance', label: 'Attendance', icon: <CalendarOutlined /> },
        canViewModule('hr') && { key: '/hr/leave', label: 'Leave', icon: <FileTextOutlined /> },
        canViewModule('hr') && { key: '/hr/holidays', label: 'Holiday Calendar', icon: <CalendarOutlined /> },
        canViewModule('hrAssets') && { key: '/hr/assets', label: 'Assets', icon: <ToolOutlined /> },
        canViewModule('hrDocuments') && { key: '/hr/documents', label: 'Documents', icon: <FileTextOutlined /> },
        canViewModule('hrPerformance') && { key: '/hr/performance', label: 'Performance', icon: <DashboardOutlined /> },
        canViewModule('hrPayroll') && { key: '/hr/payroll', label: 'Payroll Prep', icon: <DollarOutlined /> },
        canViewModule('hrExit') && { key: '/hr/exits', label: 'Exit', icon: <SettingOutlined /> },
        canViewModule('hrDashboard') && { key: '/hr/reports', label: 'Reports', icon: <FileTextOutlined /> },
      ].filter(Boolean),
    },
    canViewModule('system') && {
      key: '/system',
      label: 'Settings',
      icon: <SettingOutlined />,
      children: [
        canViewField('system', 'backup') && { key: '/system/backup-restore', label: 'Backup & Restore', icon: <SyncOutlined /> },
        canViewField('system', 'generalSettings') && { key: '/system/general-settings', label: 'General Settings', icon: <SettingOutlined /> },
        canViewField('system', 'apiConfig') && { key: '/system/api-config', label: 'API Configuration', icon: <ApiOutlined /> },
        canViewField('system', 'karatDeductions') && { key: '/system/karat-deductions', label: 'Karat Rate Deductions', icon: <GoldOutlined /> },
        canViewField('system', 'karatDeductions') && { key: '/system/silver-rates', label: 'Silver Rates', icon: <GoldOutlined /> },
        canViewField('system', 'apiConfig') && { key: '/system/website-deployment', label: 'Website Deployment', icon: <CloudUploadOutlined /> },
      ].filter(Boolean),
    },
  ].filter(Boolean);

  useEffect(() => {
    refreshProfile();
  }, []);

  // Responsive: show Drawer on mobile, Sider on desktop
  const [mobileOpen, setMobileOpen] = React.useState(false);

  // Detect mobile width
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent flicker by keeping header height fixed and font size small
  const headerStyle = {
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'space-between',
    padding: collapsed ? 0 : '0 16px',
    borderBottom: '1px solid #f0f0f0',
    background: '#fff',
    transition: 'none',
    fontSize: 14,
    minHeight: 64,
  };

  const menuContent = (
    <Menu
      mode="inline"
      selectedKeys={[location.pathname]}
      defaultSelectedKeys={['/']}
      style={{
        borderRight: 0,
        fontSize: 13,
        background: 'transparent',
      }}
      inlineCollapsed={collapsed && !isMobile}
    >
      {menuItems.map((item) =>
        item.children ? (
          <Menu.SubMenu key={item.key} icon={item.icon} title={<span style={{ fontSize: 13 }}>{item.label}</span>}>
            {item.children.map((child) => (
              <Menu.Item key={child.key} icon={child.icon}>
                <NavLink to={child.key} style={{ fontSize: 13 }}>{child.label}</NavLink>
              </Menu.Item>
            ))}
          </Menu.SubMenu>
        ) : (
          <Menu.Item key={item.key} icon={item.icon}>
            <NavLink to={item.key} style={{ fontSize: 13 }}>{item.label}</NavLink>
          </Menu.Item>
        )
      )}
    </Menu>
  );

  return (
    <>
      {/* Mobile Drawer */}
      {isMobile ? (
        <>
          <Drawer
            placement="left"
            closable={false}
            onClose={() => setMobileOpen(false)}
            open={mobileOpen}
            bodyStyle={{
              padding: 0,
              background: '#fff',
            }}
            width={drawerWidth}
          >
            <div style={headerStyle}>
              <Typography.Text strong style={{ fontSize: 15 }}>Auriksha CRM</Typography.Text>
              <Button
                type="text"
                onClick={() => setMobileOpen(false)}
                icon={<MenuFoldOutlined />}
              />
            </div>
            {menuContent}
          </Drawer>
          <Button
            type="primary"
            className="d-md-none"
            style={{
              position: 'fixed',
              top: 16,
              left: 16,
              zIndex: 1100,
              borderRadius: '50%',
              width: 40,
              height: 40,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              display: mobileOpen ? 'none' : 'block',
              fontSize: 18,
              padding: 0,
            }}
            icon={<MenuUnfoldOutlined style={{ fontSize: 18 }} />}
            onClick={() => setMobileOpen(true)}
          />
        </>
      ) : (
        <Sider
          collapsible
          collapsed={collapsed}
          width={drawerWidth}
          collapsedWidth={collapsedWidth}
          trigger={null}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            background: 'linear-gradient(135deg, #ffffffff 0%, #fbfbfbff 100%)',
            borderRight: '1px solid #f0f0f0',
            transition: 'width 0.3s',
            zIndex: 1000,
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          }}
        >
          <div style={headerStyle}>
            {!collapsed && <Typography.Text strong style={{ fontSize: 15 }}>Auriksha CRM</Typography.Text>}
            <Button
              type="text"
              onClick={onToggle}
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              style={{ fontSize: 16 }}
            />
          </div>
          {menuContent}



        </Sider>
      )}
    </>
  );
};

export default Sidebar;
